import React, { useEffect, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  EVInputLabel, EVSelect, EVFormHelperText, EVMenuItem, EVGrid, EVKeyboardArrowDown,
} from '@eagleview/ev-comp-library';
import useStyles from './FormSelectStyle';

/**
 * Component to be used as dropdown form field.
 */
const FormSelectField = forwardRef((props, ref) => {
  const {
    labelText, id, selectId, selectValue, handleSelectChange, errorMessage, selectList, showDefaultOption, defaultOption, isDisabled, isRequired,
    handleBlur,
  } = props;

  const mountedRef = useRef(false);

  const classes = useStyles();

  const handleChange = (event) => handleSelectChange({ ...event, target: { ...event.target, id } });

  useEffect(() => {
    if (mountedRef.current) {
      handleBlur({
        target: {
          id,
        },
      });
    }
  }, [selectValue]);

  useEffect(() => {
    mountedRef.current = true;
  }, []);
  const selectInputId = `select${id}`;
  return (
    <EVGrid className={classes.inputContainer}>
      <EVInputLabel
        className={classes.label}
        style={{ wordBreak: 'break-word' }}
        required={isRequired}
        classes={{ asterisk: classes.asterisk }}
        error={errorMessage.length > 0}
        id={id}
      >
        {labelText}
      </EVInputLabel>
      <EVSelect
        labelId={id}
        id={selectInputId}
        value={selectValue}
        fullWidth
        size="small"
        className={classes.select}
        variant="outlined"
        disabled={isDisabled}
        onChange={handleChange}
        error={errorMessage.length > 0}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
        }}
        IconComponent={EVKeyboardArrowDown}
        ref={ref}
      >
        {showDefaultOption && <EVMenuItem key={0} value={0}>{`- Select ${defaultOption}-`}</EVMenuItem>}
        {selectList.length > 0 && (selectList.map((selectListItem) => (
          <EVMenuItem
            key={`${selectListItem[selectId]}`}
            value={selectListItem[selectId]}
          >
            {selectListItem.name}
          </EVMenuItem>
        )))}
      </EVSelect>
      <EVFormHelperText className={classes.error}>{errorMessage}</EVFormHelperText>
    </EVGrid>
  );
});

FormSelectField.propTypes = {
  /**
   * @property {string} id - Id of EVSelect
   */
  id: PropTypes.string.isRequired,
  /**
   * @property {string} labelText - Text to be used as label
   */
  labelText: PropTypes.string,
  /**
   * @property {string} selectId - Id to select value from list
   */
  selectId: PropTypes.string,
  /**
   * @property {number} selectValue - Value of EVSelect
   */
  selectValue: PropTypes.number,
  /**
   * @property {Function} handleSelectChange - Function to handle change in selected value in EVSelect
   */
  handleSelectChange: PropTypes.func.isRequired,
  /**
   * @property {Function} handleBlur - Function to handle blur in EVSelect
   */
  handleBlur: PropTypes.func,
  /**
   * @property {array} selectList - Function to handle change in selected value in EVSelect
   */
  selectList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  /**
   * @property {string} errorMessage - Error message to be displayed by helper text
   */
  errorMessage: PropTypes.string,
  /**
   * @property {bool} showDefaultOption - flag to decide whether default option should be shown or not
   */
  showDefaultOption: PropTypes.bool,
  /**
  * @property {bool} isDisabled - boolean value to disable the component
   */
  isDisabled: PropTypes.bool,
  /**
  * @property {bool} isRequired - isRequired for component
   */
  isRequired: PropTypes.bool,
  /**
  * @property {string} defaultOption - default option to show when nothing is selected
   */
  defaultOption: PropTypes.string,
};

FormSelectField.defaultProps = {
  selectId: 'id',
  selectValue: 0,
  showDefaultOption: true,
  isRequired: false,
  isDisabled: false,
  labelText: '',
  defaultOption: '',
  errorMessage: '',
  handleBlur: () => {
    //
  },
};

export default FormSelectField;
